import React, { useState, useRef, useEffect, useCallback } from 'react';
import LoadingSpinner from './LoadingSpinner';

// Server base URL for API calls
import { API_BASE } from '../config';
import { useToast } from '../context/ToastContext';
import ConfirmModal from './ui/ConfirmModal';

function MediaUpload({ 
  onImagesChange, 
  onVideoChange, 
  onThumbnailChange,
  initialImages = [],
  initialVideo = null,
  initialThumbnail = null 
}) {
  const toast = useToast();
  const [images, setImages] = useState(initialImages);
  const [video, setVideo] = useState(initialVideo);
  const [thumbnailIndex, setThumbnailIndex] = useState(
    initialThumbnail && initialImages.length > 0 
      ? initialImages.findIndex(img => img.url === initialThumbnail) 
      : (initialImages.length > 0 ? 0 : -1)
  );
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showGoogleDriveInput, setShowGoogleDriveInput] = useState(false);
  const [googleDriveUrl, setGoogleDriveUrl] = useState('');
  const [showVideoUpload, setShowVideoUpload] = useState(true);
  const [confirmVideoReplace, setConfirmVideoReplace] = useState({ open: false, pendingFile: null });
  const [confirmGDrive, setConfirmGDrive] = useState(false);

  const imageInputRef = useRef();
  const videoInputRef = useRef();

  // Update state when initial props change (important for edit mode)
  useEffect(() => {
    setImages(initialImages || []);
  }, [initialImages]);

  useEffect(() => {
    setVideo(initialVideo);
    // Always show video upload options. If the listing is new (no initialVideo), users will see
    // the upload buttons by default.
    setShowVideoUpload(true);
  }, [initialVideo]);

  useEffect(() => {
    if (initialThumbnail && initialImages && initialImages.length > 0) {
      const thumbIndex = initialImages.findIndex(img => img.url === initialThumbnail);
      setThumbnailIndex(thumbIndex >= 0 ? thumbIndex : 0);
    } else if (initialImages && initialImages.length > 0) {
      setThumbnailIndex(0);
    } else {
      setThumbnailIndex(-1);
    }
  }, [initialThumbnail, initialImages]);

  // Handle image file selection
  const handleImageSelect = async (files) => {
    const fileArray = Array.from(files);
    
    if (images.length + fileArray.length > 5) {
      toast.warning('Maximum 5 images allowed');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      fileArray.forEach((file) => {
        formData.append('images', file);
      });

      const response = await fetch(`${API_BASE}/media/upload/images`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload response error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const result = await response.json();

      if (result.success) {
        const newImages = result.data.map(img => ({
          url: img.url,
          publicId: img.publicId,
          isThumnail: false
        }));

        const updatedImages = [...images, ...newImages];
        setImages(updatedImages);
        
        // Set first image as thumbnail if none selected
        if (updatedImages.length > 0 && thumbnailIndex === -1) {
          setThumbnailIndex(0);
          onThumbnailChange(updatedImages[0].url);
        }

        onImagesChange(updatedImages);
      } else {
        toast.warning(result.error || 'Failed to upload images');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.warning('Failed to upload images: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Handle video file selection
  const handleVideoSelect = async (file) => {
    console.log('📹 Video upload started:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      hasExistingVideo: !!(video && video.url),
      isEditMode: !!initialVideo,
      showVideoUpload: showVideoUpload
    });

    try {
      // If there's already a video, ask user if they want to replace it
      if (video && video.url) {
        setConfirmVideoReplace({ open: true, pendingFile: file });
        return;
      }

      // Check file size before upload (100MB = 100 * 1024 * 1024 bytes)
      const maxVideoSize = 100 * 1024 * 1024; // 100MB
      
      if (file.size > maxVideoSize) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
        console.log('❌ Video file too large:', fileSizeMB + 'MB');
        toast.warning(`Video file is too large (${fileSizeMB}MB). Maximum size allowed is 100MB.\n\nFor videos larger than 100MB, please upload to Google Drive and use the "Use Google Drive Link" option instead.`);
        setShowGoogleDriveInput(true);
        return;
      }

      // Check file type
      if (!file.type.startsWith('video/')) {
        console.log('❌ Invalid file type:', file.type);
        toast.warning('Please select a valid video file.');
        return;
      }

      console.log('✅ Video validation passed, starting upload...');
      setUploading(true);

      const formData = new FormData();
      formData.append('video', file);

      console.log('📤 Sending video to server...');
      const response = await fetch(`${API_BASE}/media/upload/video`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      console.log('📥 Server response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Server responded with error:', response.status, errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('📥 Server response data:', result);

      if (result.success) {
        const newVideo = {
          url: result.data.url,
          publicId: result.data.publicId,
          type: 'upload',
          thumbnail: result.data.thumbnail,
          duration: result.data.duration
        };

        console.log('✅ Video upload successful, updating state:', newVideo);
        
        // Update video state
        setVideo(newVideo);
        
        // Notify parent component
        console.log('📞 Calling onVideoChange callback');
        onVideoChange(newVideo);
        
        // Update UI state
        setShowGoogleDriveInput(false);
        setShowVideoUpload(true);
        
        console.log('🎉 Video upload process completed successfully');
      } else if (result.useGoogleDrive) {
        console.log('⚠️ Server suggests Google Drive:', result.message);
        toast.warning(result.message || 'Video file is too large. Please use Google Drive link instead.');
        setShowGoogleDriveInput(true);
      } else {
        console.log('❌ Server error:', result.error);
        throw new Error(result.error || 'Failed to upload video');
      }
    } catch (error) {
      console.error('💥 Video upload error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      // Check if it's a network/timeout error for large files
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.log('🌐 Network/timeout error detected');
        toast.warning('Upload failed - the video file might be too large or there was a network issue.\n\nFor large videos, please use the Google Drive link option instead.');
        setShowGoogleDriveInput(true);
      } else {
        toast.warning('Failed to upload video: ' + error.message);
      }
    } finally {
      console.log('🏁 Video upload process finally block - resetting uploading state');
      setUploading(false);
    }
  };

  // Handle Google Drive URL submission
  const handleGoogleDriveSubmit = () => {
    if (!googleDriveUrl.trim()) {
      toast.warning('Please enter a Google Drive URL');
      return;
    }
    // Open confirm modal — actual processing happens in executeGoogleDriveSubmit
    setConfirmGDrive(true);
  };

  const executeGoogleDriveSubmit = async () => {
    try {
      const response = await fetch(`${API_BASE}/media/validate-drive-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: googleDriveUrl }),
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        const newVideo = { url: googleDriveUrl, publicId: null, type: 'google-drive' };
        setVideo(newVideo);
        onVideoChange(newVideo);
        setShowGoogleDriveInput(false);
        setGoogleDriveUrl('');
        setShowVideoUpload(true);
      } else {
        toast.warning(result.error || 'Invalid Google Drive URL');
      }
    } catch (error) {
      console.error('Google Drive validation error:', error);
      toast.warning('Failed to validate Google Drive URL');
    }
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    // Some browsers report no MIME type for HEIC (iPhone) photos, so also match by extension
    const imageFiles = files.filter(file =>
      file.type.startsWith('image/') || /\.(heic|heif)$/i.test(file.name)
    );
    const videoFiles = files.filter(file => file.type.startsWith('video/'));

    // Handle image files
    if (imageFiles.length > 0) {
      if (images.length >= 5) {
        toast.warning('You already have the maximum number of images (5). Please remove some images first.');
        return;
      }
      handleImageSelect(imageFiles);
    }

    // Handle video files
    if (videoFiles.length > 0) {
      if (videoFiles.length > 1) {
        toast.warning('Please drop only one video file at a time.');
        return;
      }
      handleVideoSelect(videoFiles[0]);
    }
  };

  // Remove image
  const removeImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    onImagesChange(updatedImages);

    // Adjust thumbnail index
    if (thumbnailIndex === index) {
      const newThumbnailIndex = updatedImages.length > 0 ? 0 : -1;
      setThumbnailIndex(newThumbnailIndex);
      onThumbnailChange(newThumbnailIndex >= 0 ? updatedImages[newThumbnailIndex].url : null);
    } else if (thumbnailIndex > index) {
      setThumbnailIndex(thumbnailIndex - 1);
    }
  };

  // Remove video
  const removeVideo = () => {
    setVideo(null);
    onVideoChange(null);
    setShowGoogleDriveInput(false);
    setGoogleDriveUrl('');
    // Keep the video upload UI visible so the user can upload or link a different video immediately.
    setShowVideoUpload(true);
  };

  // Select thumbnail
  const selectThumbnail = (index) => {
    setThumbnailIndex(index);
    onThumbnailChange(images[index].url);
  };

  const executeVideoReplace = (file) => {
    setConfirmVideoReplace({ open: false, pendingFile: null });
    handleVideoSelect(file);
  };

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h3 style={{
        color: '#881c1c',
        marginBottom: '1rem',
        fontSize: '1.1rem',
        fontWeight: '600'
      }}>
        Add Media
      </h3>

      {/* Images Section */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{
          display: 'block',
          marginBottom: '0.5rem',
          fontWeight: '600',
          color: '#2c3e50',
          fontSize: '0.95rem'
        }}>
          Images * (1-3 required)
        </label>

        {/* Drag and Drop Area */}
        <div
          style={{
            border: `2px dashed ${dragOver ? '#881c1c' : '#e4e3e2'}`,
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            backgroundColor: dragOver ? 'rgba(136, 28, 28, 0.05)' : 
                           images.length >= 5 ? '#f5f5f5' : '#f0efeb',
            cursor: images.length >= 5 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            marginBottom: '1rem',
            opacity: images.length >= 5 ? 0.6 : 1
          }}
          onDragOver={images.length < 5 ? handleDragOver : undefined}
          onDragLeave={images.length < 5 ? handleDragLeave : undefined}
          onDrop={images.length < 5 ? handleDrop : undefined}
          onClick={images.length < 5 ? () => imageInputRef.current?.click() : undefined}
        >
          <div style={{ color: '#6b6a64', marginBottom: '0.5rem' }}>
            {images.length >= 5 ? 
              'Maximum images reached (5/5)' : 
              'Drag & drop images here, or click to select'
            }
          </div>
          <div style={{ fontSize: '0.8rem', color: '#888' }}>
            {images.length >= 5 ? 
              'Remove an image to add more' :
              `JPEG, PNG, WebP, GIF, HEIC • Max 10MB each • ${images.length}/5 images`
            }
          </div>
        </div>

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*,.heic,.heif"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleImageSelect(e.target.files)}
          disabled={images.length >= 5}
        />

        {/* Image Previews */}
        {images.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '1rem'
            }}>
              {images.map((image, index) => (
                <div key={index} style={{
                  position: 'relative',
                  border: thumbnailIndex === index ? '3px solid #881c1c' : '2px solid #e4e3e2',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: 'white'
                }}>
                  <img
                    src={image.url}
                    alt={`Upload ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '120px',
                      objectFit: 'cover'
                    }}
                  />
                  
                  {/* Thumbnail Badge */}
                  {thumbnailIndex === index && (
                    <div style={{
                      position: 'absolute',
                      top: '5px',
                      left: '5px',
                      backgroundColor: '#881c1c',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: '600'
                    }}>
                      THUMBNAIL
                    </div>
                  )}

                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    style={{
                      position: 'absolute',
                      top: '5px',
                      right: '5px',
                      backgroundColor: '#881c1c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    ×
                  </button>

                  {/* Set as Thumbnail Button */}
                  {thumbnailIndex !== index && (
                    <button
                      type="button"
                      onClick={() => selectThumbnail(index)}
                      style={{
                        position: 'absolute',
                        bottom: '5px',
                        left: '5px',
                        right: '5px',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px',
                        cursor: 'pointer',
                        fontSize: '0.7rem'
                      }}
                    >
                      Set as Thumbnail
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Video Section */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{
          display: 'block',
          marginBottom: '0.5rem',
          fontWeight: '600',
          color: '#2c3e50',
          fontSize: '0.95rem'
        }}>
          Video (Optional)
        </label>

        {/* Show upload options when video section is active */}
        {((!video || !video.url) && !showGoogleDriveInput && showVideoUpload) && (
          <div>
            {/* File size info */}
            <div style={{
              fontSize: '0.8rem',
              color: '#6b6a64',
              marginBottom: '0.75rem',
              padding: '0.5rem',
              backgroundColor: '#f0efeb',
              borderRadius: '4px',
              border: '1px solid #e4e3e2'
            }}>
              MP4, MOV, AVI, WebM • Max 100MB • For larger files, use Google Drive link
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                disabled={uploading}
                style={{
                  padding: '0.75rem 1.25rem',
                  backgroundColor: 'transparent',
                  color: '#881c1c',
                  border: '2px solid #881c1c',
                  borderRadius: '8px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  opacity: uploading ? 0.6 : 1
                }}
                onMouseOver={(e)=>{ if(!uploading){e.target.style.background='#881c1c'; e.target.style.color='#fff';}}}
                onMouseOut={(e)=>{ if(!uploading){e.target.style.background='transparent'; e.target.style.color='#881c1c';}}}
              >
                Upload Video
              </button>
              
              <button
                type="button"
                onClick={() => setShowGoogleDriveInput(true)}
                style={{
                  padding: '0.75rem 1.25rem',
                  backgroundColor: 'transparent',
                  color: '#881c1c',
                  border: '2px solid #881c1c',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
                onMouseOver={(e)=>{e.target.style.background='#881c1c'; e.target.style.color='#fff';}}
                onMouseOut={(e)=>{e.target.style.background='transparent'; e.target.style.color='#881c1c';}}
              >
                Google Drive
              </button>
            </div>

            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              style={{ display: 'none' }}
              onChange={(e) => e.target.files[0] && handleVideoSelect(e.target.files[0])}
            />
          </div>
        )}

        {/* Show Google Drive option when video exists */}
        {(video && video.url) && !showGoogleDriveInput && (
          <div style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              onClick={() => setShowGoogleDriveInput(true)}
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: 'transparent',
                color: '#881c1c',
                border: '2px solid #881c1c',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginRight: '0.5rem'
              }}
            >
              Add Google Drive Link
            </button>
            <span style={{ fontSize: '0.85rem', color: '#6b6a64' }}>
              Add a Google Drive link for this listing
            </span>
          </div>
        )}

        {/* Google Drive URL Input */}
        {showGoogleDriveInput && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f0efeb',
            borderRadius: '8px',
            border: '1px solid #e4e3e2',
            marginBottom: (video && video.url) ? '1rem' : '0'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#881c1c' }}>
              Add Google Drive Video Link
            </h4>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#6b6a64' }}>
              <strong>Instructions:</strong><br/>
              1. Upload your video to Google Drive<br/>
              2. Right-click the video → Get link → Change to "Anyone with the link"<br/>
              3. Copy and paste the link below
            </p>
            <input
              type="url"
              value={googleDriveUrl}
              onChange={(e) => setGoogleDriveUrl(e.target.value)}
              placeholder="https://drive.google.com/file/d/..."
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #ddd',
                marginBottom: '0.5rem'
              }}
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={handleGoogleDriveSubmit}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Add Link
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowGoogleDriveInput(false);
                  setGoogleDriveUrl('');
                  // Keep the video upload section visible when cancelling the Drive input so
                  // users can still choose to upload a file instead.
                  setShowVideoUpload(true);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Video Preview with Remove Button - ONLY show if video actually exists */}
        {(video && video.url) && (
          <div style={{
            border: '2px solid #e4e3e2',
            borderRadius: '8px',
            padding: '1rem',
            backgroundColor: 'white'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem'
            }}>
              <span style={{ fontWeight: '600', color: '#881c1c' }}>
                {video.type === 'google-drive' ? 'Google Drive Video' : 'Uploaded Video'}
              </span>
              <button
                type="button"
                onClick={removeVideo}
                style={{
                  backgroundColor: '#881c1c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}
              >
                Remove Video
              </button>
            </div>
            
            {video.type === 'upload' ? (
              <video
                controls
                style={{
                  width: '100%',
                  maxHeight: '200px',
                  borderRadius: '4px'
                }}
              >
                <source src={video.url} />
                Your browser does not support video playback.
              </video>
            ) : (
              <div style={{
                padding: '1rem',
                backgroundColor: '#f0efeb',
                borderRadius: '4px',
                textAlign: 'center'
              }}>
                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#881c1c',
                    textDecoration: 'none',
                    fontWeight: '500'
                  }}
                >
                  View Video on Google Drive
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div style={{ textAlign:'center', margin:'1.5rem 0' }}>
          <LoadingSpinner />
          <div style={{ marginTop:'0.75rem', fontSize:'0.9rem', color:'#6b6a64' }}>Uploading media...</div>
        </div>
      )}

      {/* Validation Message */}
      {images.length === 0 && (
        <div style={{
          padding: '0.75rem',
          backgroundColor: 'rgba(136, 28, 28, 0.05)', // light maroon tint
          borderRadius: '8px',
          color: '#881c1c',
          fontSize: '0.9rem',
          border: '1px solid rgba(136, 28, 28, 0.2)'
        }}>
          At least one image is required
        </div>
      )}

      {/* Confirm modals for video actions */}
      <ConfirmModal
        open={confirmVideoReplace.open}
        title="Replace existing video?"
        message="You already have a video attached. Adding a new one will replace it."
        confirmLabel="Replace"
        danger
        onConfirm={() => executeVideoReplace(confirmVideoReplace.pendingFile)}
        onCancel={() => setConfirmVideoReplace({ open: false, pendingFile: null })}
      />
      <ConfirmModal
        open={confirmGDrive}
        title="Add Google Drive video?"
        message="This will link the Google Drive video to your listing."
        confirmLabel="Add Video"
        onConfirm={() => { setConfirmGDrive(false); executeGoogleDriveSubmit(); }}
        onCancel={() => setConfirmGDrive(false)}
      />
    </div>
  );
}

export default MediaUpload; 